import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Tooltip, Skeleton } from '@mui/material';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import { ICONS } from '../../../shared/icons';
import { FILTER_VALUES, PROJECT_TYPES, SPRINT_TIMELINE_STATUS } from '../../../shared/appConstants';
import { fetchProjectDropdownsByUser, setFilters } from '../../../store/ticketSlice';
import { fetchSprintManagement } from '../../../store/scrumSlice';
import refreshIcon from '../../../assets/images/refresh_icon.svg';
import SprintSelector from './components/SprintSelector';
import SprintHealthCard from './components/SprintHealthCard';
import SprintLoadChart from './components/SprintLoadChart';
import BurndownChart from './components/BurndownChart';
import AtRiskTickets from './components/AtRiskTickets';
import SprintSummaryGrids from './components/SprintSummaryGrids';
import Navbar from '../../shared/Navbar';
import './SprintManagement.scss';

const SprintManagementBodySkeleton = ({ className = '' }) => (
  <div className={`sprint-management-body ${className}`.trim()}>
    <div className="sprint-health-container">
      <div className="sm-card goal-card">
        <div className="sm-card-header-with-icon">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={120} height={28} />
        </div>
        <Skeleton variant="text" width="100%" height={24} />
        <Skeleton variant="text" width="80%" height={24} />
      </div>
      <div className="sm-card health-card">
        <div className="health-content">
          <div className="circular-progress-section">
            <Skeleton variant="text" width={120} height={28} />
            <Skeleton variant="circular" width={140} height={140} className="skeleton-my-1" />
            <Skeleton variant="rectangular" width={100} height={32} className="skeleton-rounded-sm" />
          </div>
          <div className="health-metrics-grid">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="metric-box">
                <div className="metric-header">
                  <Skeleton variant="text" width={80} height={24} />
                  <Skeleton variant="text" width={40} height={32} />
                </div>
                <Skeleton variant="rectangular" width="100%" height={10} className="skeleton-mt-2 skeleton-rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    <div className="sprint-load-row">
      <div className="sm-card sprint-load-card">
        <div className="sm-card-header-with-icon">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={150} height={28} />
        </div>
        <Skeleton variant="rectangular" width="100%" height={240} className="skeleton-rounded-md" />
      </div>
    </div>

    <div className="charts-row">
      <div className="sm-card">
        <div className="sm-card-header-with-icon">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={150} height={28} />
        </div>
        <Skeleton variant="rectangular" width="100%" height={240} className="skeleton-rounded-md" />
      </div>
      <div className="sm-card at-risk-card">
        <div className="card-header">
          <div className="title-group">
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={150} height={28} />
          </div>
        </div>
        <div className="list-container">
          {[1, 2, 3].map(i => (
            <div key={i} className="ticket-row">
              <div className="left-column skeleton-col-60">
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="40%" height={16} />
              </div>
              <div className="right-column skeleton-col-30">
                <Skeleton variant="rectangular" width={60} height={20} className="skeleton-chip" />
                <Skeleton variant="text" width={80} height={16} className="skeleton-mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SprintManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedSprintStatus, setSelectedSprintStatus] = useState(SPRINT_TIMELINE_STATUS.ACTIVE);
  const { projectDropdownsByUser, projectDropdownsByUserLoading, filters } = useSelector((state) => state.tickets);
  const { sprintManagement, sprintManagementLoading, sprintManagementError } = useSelector((state) => state.scrum);

  const selectedProject = useMemo(() => {
    const selectedProjectId = filters?.selectedProject;
    if (!selectedProjectId || String(selectedProjectId).toUpperCase() === String(FILTER_VALUES.ALL)) return null;
    return projectDropdownsByUser.find((project) => String(project.id) === String(selectedProjectId)) || null;
  }, [filters?.selectedProject, projectDropdownsByUser]);

  const projectId = selectedProject?.id || '';
  const snapshot = sprintManagement?.snapshot || null;
  const selectedSprint = snapshot?.[selectedSprintStatus] || null;

  useEffect(() => {
    if (!projectDropdownsByUser.length) dispatch(fetchProjectDropdownsByUser());
  }, [dispatch, projectDropdownsByUser.length]);

  useEffect(() => {
    if (projectDropdownsByUserLoading || !projectDropdownsByUser.length) return;
    const isSelectedScrumProject = selectedProject &&
      String(selectedProject.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
    if (isSelectedScrumProject) return;

    const scrumProject = projectDropdownsByUser.find((project) =>
      String(project.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase()
    );
    if (scrumProject?.id) dispatch(setFilters({ selectedProject: scrumProject.id }));
  }, [dispatch, projectDropdownsByUser, projectDropdownsByUserLoading, selectedProject]);

  useEffect(() => {
    if (projectId) dispatch(fetchSprintManagement({ projectId, status: selectedSprintStatus }));
  }, [dispatch, projectId, selectedSprintStatus]);

  useEffect(() => {
    if (!snapshot || snapshot[selectedSprintStatus]?.id) return;
    const fallbackStatus = [
      SPRINT_TIMELINE_STATUS.ACTIVE,
      SPRINT_TIMELINE_STATUS.COMPLETED,
      SPRINT_TIMELINE_STATUS.DRAFT,
    ].find((status) => snapshot[status]?.id);
    if (fallbackStatus) setSelectedSprintStatus(fallbackStatus);
  }, [selectedSprintStatus, snapshot]);

  const handleRefresh = useCallback(() => {
    if (projectId) dispatch(fetchSprintManagement({ projectId, status: selectedSprintStatus }));
  }, [dispatch, projectId, selectedSprintStatus]);

  const healthData = useMemo(() => ({ data: { ...snapshot, details: sprintManagement?.health } }), [snapshot, sprintManagement]);
  const chartsData = useMemo(() => ({ data: sprintManagement?.charts }), [sprintManagement]);
  const summaryData = useMemo(() => ({ data: sprintManagement?.summary }), [sprintManagement]);
  const [isDownloading, setIsDownloading] = useState(false);

  const isExecutionDataEmpty = useMemo(() => {
    const teamLoadData = chartsData?.data?.team_load?.data || [];
    const isTeamLoadEmpty = teamLoadData.length === 0 || teamLoadData.every(d => Number(d?.tickets ?? 0) === 0 && Number(d?.sp ?? 0) === 0);
    const burnChartData = chartsData?.data?.burn_chart || {};
    const isBurnChartEmpty = Object.keys(burnChartData).length === 0 || Object.values(burnChartData).every(d => (d?.expected ?? 0) === 0 && (d?.achieved ?? 0) === 0);
    const isRiskTicketsEmpty = !chartsData?.data?.risk_tickets || chartsData.data.risk_tickets.length === 0;
    
    const spSummary = summaryData?.data?.sp_summary || {};
    const isSpSummaryEmpty = Object.keys(spSummary).length === 0 || Object.values(spSummary).every(val => val === null || val === undefined);
    
    const ticketHealth = summaryData?.data?.ticket_health || {};
    const isTicketHealthEmpty = Object.keys(ticketHealth).length === 0 || Object.values(ticketHealth).every(val => val === null || val === undefined);

    return isTeamLoadEmpty && isBurnChartEmpty && isRiskTicketsEmpty && isSpSummaryEmpty && isTicketHealthEmpty;
  }, [chartsData?.data, summaryData?.data]);
  const [areChartsReady, setAreChartsReady] = useState(false);

  // Enable export button after 3 seconds of loading finishing to allow charts to render and animations to complete
  useEffect(() => {
    setAreChartsReady(false);
    if (!sprintManagementLoading && selectedSprint?.id) {
      const timeout = setTimeout(() => {
        setAreChartsReady(true);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [selectedSprint?.id, sprintManagementLoading]);

  const handleExportPDF = async () => {
    setIsDownloading(true);
    try {
      const tables = [];
      const riskTickets = chartsData?.data?.risk_tickets || [];
      
      if (riskTickets.length > 0) {
        tables.push({
          title: t('sprintManagement.atRiskTicketsTitle', 'At Risk Tickets'),
          headers: [
            { label: 'Title', key: 'title' },
            { label: 'Assignee', key: 'assignee' },
            { label: 'Due Date', key: 'dueDate' },
            { label: 'Status', key: 'status' },
            { label: 'Priority', key: 'priority' }
          ],
          data: riskTickets.map(row => {
            const ticketPrefix = row.project_code ? `${row.project_code}-` : '';
            const ticketNum = row.ticket_number ? row.ticket_number : '';
            const ticketIdStr = (ticketPrefix || ticketNum) ? `${ticketPrefix}${ticketNum}\n` : '';
            
            return {
              title: `${ticketIdStr}${row.title}`,
              assignee: (row.assignee || []).length > 0 
                  ? row.assignee.join(', ') 
                  : t('sprintManagement.unassigned', 'Unassigned'),
              dueDate: row.due_date ? new Date(row.due_date).toLocaleDateString() : '-',
              status: row.status || '-',
              priority: row.priority || '-'
            };
          })
        });
      }

      const { exportReportsDashboardToPDF } = await import('../../Reports/reportExportUtils');
      await exportReportsDashboardToPDF({
        mainSelector: '.sprint-management-main-container',
        tables,
        filename: `sprint-management-${new Date().toISOString().split('T')[0]}.pdf`,
      });
    } catch (error) {
      console.error('Error exporting Sprint Management PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="dashboard">
      <Navbar />
      <main className="main sprint-management-main-container">
        <div className="sprint-management-header">
          <h1>{t('sprintManagement.title')}</h1>
          <div className="header-actions">
            <Tooltip title={t('dashboard.refresh', 'Refresh')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <IconButton className="dashboard-header-icon-button" aria-label={t('dashboard.refresh', 'Refresh')} onClick={handleRefresh} disabled={!projectId || sprintManagementLoading}>
                <img src={refreshIcon} alt="refresh" className="dashboard-header-icon dashboard-header-icon-refresh" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('dashboard.download', 'Download')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <span>
                <IconButton 
                  className={`dashboard-header-icon-button ${(!areChartsReady || isDownloading || isExecutionDataEmpty) ? 'dashboard-header-icon-button--disabled' : ''}`}
                  aria-label={t('dashboard.download', 'Download')}
                  onClick={handleExportPDF}
                  disabled={!areChartsReady || isDownloading || isExecutionDataEmpty}
                >
                  <img src={ICONS.download} alt="download" className={`dashboard-header-icon ${(!areChartsReady || isDownloading || isExecutionDataEmpty) ? 'icon-disabled' : ''}`} style={{ width: 18, height: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      <div className="sprint-management-content">
        {(projectDropdownsByUserLoading || (sprintManagementLoading && !snapshot)) ? (
          <div className="skeleton-wrapper">
            <div className="sm-card sprint-selector-card skeleton-mb-3">
              <div className="sm-card-header-with-icon">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={200} height={28} />
              </div>
              <div className="stepper-container">
                {[1, 2, 3].map(i => (
                  <div key={i} className="stepper-step">
                    <div className="step-indicator">
                      <div className="circle circle-draft"><div className="inner-circle" /></div>
                    </div>
                    <div className="sm-stepper-card draft-card">
                      <div className="card-header">
                        <Skeleton variant="text" width={120} height={20} />
                        <Skeleton variant="rectangular" width={60} height={20} className="skeleton-chip" />
                      </div>
                      <div className="card-dates">
                        <Skeleton variant="text" width={140} height={16} />
                      </div>
                      <div className="card-footer footer-push">
                        <Skeleton variant="text" width={100} height={16} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <SprintManagementBodySkeleton />
          </div>
        ) : sprintManagementError && !snapshot ? (
          <div className="sprint-management-state-message">{sprintManagementError}</div>
        ) : !projectId ? (
          <div className="sprint-management-state-message">{t('sprintManagement.noDataAvailable')}</div>
        ) : (
          <>
        <SprintSelector 
          selectedSprintStatus={selectedSprintStatus}
          onSelectSprint={setSelectedSprintStatus}
          apiData={{ data: snapshot }}
        />
        
        {sprintManagementLoading ? (
          <SprintManagementBodySkeleton className="skeleton-wrapper" />
        ) : sprintManagementError ? (
          <div className="sprint-management-state-message sprint-management-state-message-details">{sprintManagementError}</div>
        ) : selectedSprint?.id && sprintManagement?.status === selectedSprintStatus ? (
          <div className="sprint-management-body">
            <SprintHealthCard sprintId={selectedSprint.id} apiData={healthData} />
            
            {isExecutionDataEmpty ? (
              <div className="sm-card sm-empty-state-card execution-empty">
                <div className="sm-empty-state-icon-wrapper execution-icon">
                  <PendingActionsOutlinedIcon />
                </div>
                <h3 className="sm-empty-state-title execution-title">{t('sprintManagement.noExecutionDataTitle')}</h3>
                <p className="sm-empty-state-desc execution-desc">
                  {t('sprintManagement.noExecutionDataDesc')}
                </p>
              </div>
            ) : (
              <>
                <div className="sprint-load-row">
                  <SprintLoadChart sprintId={selectedSprint.id} apiData={chartsData} />
                </div>
                
                <div className="charts-row">
                  <BurndownChart sprintId={selectedSprint.id} apiData={chartsData} />
                  <AtRiskTickets sprintId={selectedSprint.id} apiData={chartsData} />
                </div>
                
                <SprintSummaryGrids sprintId={selectedSprint.id} apiData={summaryData} />
              </>
            )}
          </div>
        ) : null}
          </>
        )}
        </div>
      </main>
    </div>
  );
};

export default SprintManagement;
